import { twJoin } from "tailwind-merge";
import BgImage from "./BgImage";
import { useParams } from "@remix-run/react";
import BookmarkButton from "./BookmarkButton";
import { useIsPrinting } from "./PrintProvider";

type Props = {
  name: string;
  thumbnail: string;
  groups?: string[];
  quality?: number;
  reversible?: boolean;
  wras?: boolean;
};

const groupColors: Record<string, string> = {
  D: "bg-[#95AFA2]",
  F: "bg-[#827580]",
  H: "bg-[#D1AF76]",
  HJ: "bg-[#677E8A]",
  HT: "bg-[#D1AF76]",
  IM: "bg-[#E39D78]",
  IS: "bg-[#E39D78]",
  IT: "bg-[#E39D78]",
  J: "bg-[#7A9CA7]",
  JS: "bg-[#7A9CA7]",
  JU: "bg-[#7A9CA7]",
  M: "bg-[#A19A8E]",
  MS: "bg-[#A19A8E]",
};

const handlePriceGroups: Record<string, string> = {
  HT: "H",
  IM: "I",
  IS: "I",
  IT: "I",
  JS: "J",
  JU: "J",
  MS: "M",
};

export default function Item(props: Props) {
  const { slug } = useParams<{ slug: string }>();
  const isWorktops = slug === "worktops";
  const groups = props.groups
    ?.filter((g) => !g.startsWith("B"))
    .map((g) => (slug === "handles" ? handlePriceGroups[g] || g : g));
  const badges = slug === "handles" ? [...new Set(groups)].sort() : groups;
  const quality = isWorktops
    ? [
        "",
        "Entry Range",
        "Low Range",
        "Mid Range",
        "High Range",
        "Premium Range",
      ]
    : ["", "Good", "Better", "Best", "Best", "Best"];
  const isPrinting = useIsPrinting();
  return (
    <div
      className={twJoin(
        "relative bg-gray-100 text-xs shadow",
        "print:border print:text-black print:shadow-none",
      )}
    >
      {isPrinting && (
        <img
          src={props.thumbnail}
          className="hidden aspect-square print:block"
        />
      )}
      <BgImage
        src={props.thumbnail}
        className="aspect-square !bg-contain print:hidden"
      />
      <div className="absolute inset-x-0 bottom-0 m-2 space-y-1">
        <div className="flex flex-wrap-reverse gap-1">
          {badges?.map((g) => (
            <div
              key={g}
              className={twJoin(
                "grid h-6 w-6 place-items-center",
                "rounded-sm font-medium text-white",
                groupColors[g === "I" ? "IM" : g] || "bg-neutral-400",
                "print:rounded-none print:border",
                "print:border-black print:text-black",
              )}
              children={g}
            />
          ))}
        </div>
        <div className="grid grid-flow-col gap-4 bg-white/90 p-2 font-light">
          <h4>{props.name}</h4>
          <div className="grid grid-flow-col gap-1 place-self-end justify-self-end">
            {Array.from({ length: props.quality || 0 }, (_, i) => (
              <div
                key={i}
                className="grid h-4 w-4 cursor-default place-items-center rounded-sm bg-neutral-400 text-center text-[9px] text-white print:text-black"
                title={quality[props.quality || 1]}
              >
                &pound;
              </div>
            ))}
            {[props.reversible && "R", props.wras && "W"]
              .filter(Boolean)
              .map((k, i) => (
                <div
                  key={i}
                  className={twJoin(
                    "group relative grid h-4 w-4 cursor-default place-items-center rounded-full",
                    "bg-neutral-400 text-[9px] font-medium text-white print:text-black",
                  )}
                >
                  <div className="absolute -right-2 bottom-full mb-2 hidden whitespace-nowrap rounded bg-neutral-600 px-2 py-1 group-hover:block">
                    <div className="absolute -bottom-[4px] right-3 h-2 w-2 rotate-45 bg-neutral-600" />
                    <div className="relative">
                      {props.reversible ? "Reversible" : ""}
                      {props.wras ? "WRAS Approved" : ""}
                    </div>
                  </div>
                  {k}
                </div>
              ))}
          </div>
        </div>
      </div>
      <BookmarkButton
        key={props.thumbnail}
        id={props.thumbnail}
        className="print:hidden"
      />
    </div>
  );
}
